import {useRouter} from 'next/router';
import {useCallback, useMemo, useState} from 'react';
import {StaticLandingOriginalMoreFetcher} from '../../../../common/fetchers/StaticLandingOriginalMoreFetcher';
import {t} from '../../../../common/getTranslations';
import {isGreaterThanZero} from '../../../../common/isGreaterThanZero';
import {requestContext} from '../../../../common/requestContext';
import {twMerge} from '../../../../common/twMerge';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {SubTabItemType} from '../../../../design-system/organisms/SubTabItem/SubTabItemType.interface';
import {StaticLandingOriginal as StaticLandingOriginalPage} from '../../../../design-system/pages/StaticLanding/StaticLandingOriginal/StaticLandingOriginal';
import {EMPTY_LANDING_PRODUCT_TEXT_KEY} from '../../../../domain/landing/constant';
import {StaticLandingOriginalSection} from '../../../../graphql/generated/types';
import {CommonSectionProps} from '../../type';

export function StaticLandingOriginal({section, children}: CommonSectionProps) {
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const router = useRouter();

  const staticLandingOriginalSection = section as Omit<
    StaticLandingOriginalSection,
    'groups'
  >;
  const {
    originalCount,
    totalCount,
    recommendItemList,
    sortTypeList,
    subcategoryList: _subcategoryList,
    param: {categoryUid, subcategory, isComplete, sortType: _sortType},
  } = staticLandingOriginalSection;
  const [isShowSortTypeDialog, setIsShowSortTypeDialog] = useState(false);

  const subcategoryList: SubTabItemType[] = useMemo(
    () =>
      _subcategoryList.map(({name, param}) => ({
        name,
        param,
        href: `/landing/original/${categoryUid}/${param}`,
      })),
    [_subcategoryList, categoryUid]
  );

  const onClickSortTypeItem = useCallback(
    (sortType: string) => {
      setIsShowSortTypeDialog(false);
      if (sortType !== _sortType?.param) {
        let pathname = `/landing/original/${categoryUid}`;
        if (subcategory) {
          pathname += `/${subcategory.param}`;
        }

        router.push(
          {
            pathname,
            query: {
              is_complete: isComplete,
              sort_opt: sortType,
            },
          },
          undefined,
          {
            scroll: false,
          }
        );
      }
    },
    [_sortType?.param, categoryUid, subcategory, router, isComplete]
  );

  const onClickCompleteOnly = useCallback(() => {
    const nextIsComplete = !isComplete;

    let pathname = `/landing/original/${categoryUid}`;
    if (subcategory) {
      pathname += `/${subcategory.param}`;
    }
    router.push(
      {
        pathname,
        query: {
          is_complete: nextIsComplete,
          ...(_sortType && {sort_opt: _sortType.param}),
        },
      },
      undefined,
      {
        scroll: false,
      }
    );
  }, [isComplete, categoryUid, subcategory, _sortType, router]);

  return (
    <StaticLandingOriginalPage
      isMobile={isMobile}
      isWebView={isWebView}
      originalCount={originalCount}
      totalCount={totalCount}
      recommendItemList={recommendItemList}
      currentCategoryUid={categoryUid}
      sortTypeList={sortTypeList}
      currentSortType={_sortType?.param}
      subcategoryList={subcategoryList}
      currentSubcategoryUid={subcategory?.param}
      isShowSortTypeDialog={isShowSortTypeDialog}
      isShowCompleteSeries={isComplete}
      onClickSortTypeTabItem={onClickSortTypeItem}
      onClickCompleteOnly={onClickCompleteOnly}
      showSortTypeDialog={() => setIsShowSortTypeDialog(true)}
      hideSortTypeDialog={() => setIsShowSortTypeDialog(false)}
    >
      {isGreaterThanZero(totalCount) && (
        <StaticLandingOriginalMoreFetcher
          section={staticLandingOriginalSection}
        >
          <div
            className={twMerge(
              'flex grow flex-col px-15pxr',
              isMobile && 'py-10pxr'
            )}
          >
            {children}
          </div>
        </StaticLandingOriginalMoreFetcher>
      )}
      {!isGreaterThanZero(totalCount) && (
        <NoItem isMobile={isMobile}>{t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}</NoItem>
      )}
    </StaticLandingOriginalPage>
  );
}
